/**
 * A plain on/off switch (`role="switch"`), for a single boolean setting — the
 * "enable / disable" control an operator reaches for instead of a bare checkbox.
 *
 * This is NOT `GlassToggle`: that is a segmented pill for a small set of
 * mutually-exclusive MODES. A boolean is one thing that is on or off, and a
 * sliding switch reads as exactly that.
 *
 * Uncontrolled label: pass `label` for the accessible name; render any visible
 * text beside the switch yourself, so the caller controls the layout.
 */
export function ToggleSwitch({
  checked,
  onChange,
  disabled,
  label,
  accent = 'bg-emerald-500',
  size = 'sm',
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  /** Accessible name (aria-label). */
  label: string
  /** Track colour when ON. */
  accent?: string
  /** `sm` (default) for dense forms; `md` for a settings row where the switch is the row's one control. */
  size?: 'sm' | 'md'
}) {
  const md = size === 'md'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex ${md ? 'h-6 w-10' : 'h-4 w-7'} shrink-0 items-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? accent : 'themed-bg-tertiary border themed-border'
      }`}
    >
      <span
        aria-hidden
        className={`inline-block ${md ? 'h-5 w-5' : 'h-3 w-3'} transform rounded-full bg-white shadow transition-transform ${
          checked ? (md ? 'translate-x-[18px]' : 'translate-x-3.5') : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}
